// Field Leadership Review — the Overview VoltaGrid tab's map of the
// superintendents, general foremen and foremen on the job (Seth 11/2: "a
// section that helps us review the foreman, general foreman, and super";
// 11/3: "two rows, one for mech and one for electrical, like a flow map to
// visualize the leadership").
//
// Who leads is JDE's own word: a person's latest Labor_Classification_
// Description on their 51100 / 51200 cost lines (voltaGridEquipment's
// labor basis — nothing here reads HeavyJob, Seth 10/29), plus anyone the
// PM adds by hand. Per person: when they came on, their last day, days and
// hours, overtime share, their last four weeks, what their wages cost and
// what those lines bill at (the Billing tab's priced lines, by employee
// number). A foreman's crew is read off their VoltaGrid Field daily
// reports over the last REPORT_DAYS days: reports filed, the crew's average
// size and hours, and how recently they filed (a green dot inside a week,
// amber inside two).
//
// The map: two lanes, Mechanical and Electrical, each a set of chains
// superintendent → general foreman → foreman. The lines are the VoltaGrid
// Field roster's Reports_To_Emp (set from whose HeavyJob card a GF's hours
// sit on, vgFieldSetup) unless the PM moves someone; a lane is the
// classification's word (Electrical, I&E, rig) or where the hours are coded
// (wbsVoltaGrid's Electrical / Instrumentation sections against the rest),
// and a chain takes its top person's lane. A dashed box is a step nobody
// fills. The roster's gf / super roles lift a person JDE calls a foreman.
//
// The review itself is the PM's, saved as 'Overview' rows on the version
// (VgLeadReview, rowId = employee number, JSON { rating, note, lane?,
// under?, role?, hidden? }, '' once cleared) like the PSR sections' rows: a
// rating and a note, who they are under and which lane where the roster is
// wrong, a role for a person added to the map by hand, and hidden for a
// lead JDE names who does not belong on the map (Seth 11/3: "how do i
// remove people" — Hide on the card; "Show N hidden" under the map brings
// them back to put them on again). Every choice saves as it is made;
// nothing here waits on a Save button.
import React, { useEffect, useMemo, useState } from 'react';
import { useAtom } from 'jotai';
import moment from 'moment';
import { pNum, fNum, loadVgFieldCrewDays } from '../db/company';
import { jobCostDetailAtom, loadingCostAtom } from '../store/company';
import { versionWeeks, laborWeeksFor } from './voltaGridEquipment';
import { voltaGridWbsFor } from '../db/wbsVoltaGrid';

export const ROLES = ['Superintendent', 'General Foreman', 'Foreman'];
export const LANES = ['Mechanical', 'Electrical'];
export const RATINGS = ['Strong', 'On track', 'Needs attention'];
export const REPORT_DAYS = 28;
const TREND_WEEKS = 4;
const NOTE_MAX = 300;
const AUTO = '__auto';

/** The leadership role a JDE classification names, or null. */
export const leadRoleOf = (cls) => {
    const s = String(cls || '').toLowerCase();
    if (!s) return null;
    if (/superintendent|\bsupt\b|\bsuper\b/.test(s)) return 'Superintendent';
    if (/general\s*foreman|\bgf\b/.test(s)) return 'General Foreman';
    if (/foreman|straw\s*boss|crew\s*lead/.test(s)) return 'Foreman';
    return null;
};
const rank = (role) => ROLES.indexOf(role);
const roleWord = (role) => (role === 'General Foreman' ? 'general foreman' : String(role || '').toLowerCase());
const rolePlural = (role) => (role === 'Foreman' ? 'Foremen' : role === 'General Foreman' ? 'General foremen' : `${role}s`);

/**
 * The lane a cost code's hours count toward: wbsVoltaGrid's Electrical and
 * Instrumentation sections, the other scope sections as Mechanical; indirects
 * neither, nor the civil work the bid files under Electrical (UG dirtwork,
 * civil/concrete, T&M), which mechanical crews dig for the electricians
 * (Taylor Swor's 230607 / 230608 hours, 11/3).
 */
const CIVIL_GROUPS = /civil|dirtwork|^t&m$/i;
export const laneOfCode = (code) => {
    const w = voltaGridWbsFor(code);
    if (!w || w.name === 'Indirects') return null;
    if (w.name === 'Electrical') return CIVIL_GROUPS.test(w.group || '') ? null : 'Electrical';
    return w.name === 'Instrumentation' ? 'Electrical' : 'Mechanical';
};
const ELECTRICAL_RE = /electric|instrument|\bi\s*&\s*e\b|\be\s*&\s*i\b/i;
const MECHANICAL_RE = /mechanical|pip(e|ing)|weld|\brig|structural|iron|civil|concrete/i;

/** The leadership role the field roster's roles grant ('super', 'gf'), or null. */
export const rosterRoleOf = (roles) => {
    const list = (Array.isArray(roles) ? roles : String(roles || '').split(',')).map((s) => String(s).trim().toLowerCase());
    if (list.includes('super')) return 'Superintendent';
    if (list.includes('gf')) return 'General Foreman';
    return null;
};
/** JDE's role, lifted to the roster's where the roster puts them higher (Jason Brunner: Foreman in JDE, gf on the roster). */
export const roleWithRoster = (role, roles) => {
    const r = rosterRoleOf(roles);
    return role && r && rank(r) < rank(role) ? r : role;
};

const ymd = (v) => {
    if (!v) return '';
    const s = String(v);
    const m = /^\d{4}-\d{2}-\d{2}/.test(s) ? moment.utc(s.slice(0, 10), 'YYYY-MM-DD') : moment.utc(s);
    return m.isValid() ? m.format('YYYY-MM-DD') : '';
};
// Employee numbers compare without their leading zeros: JDE pads some, the
// field app stores the address book number as typed.
export const empKey = (id) => String(id == null ? '' : id).trim().replace(/^0+(?=\d)/, '');

/**
 * Everyone with labor on the job, off the cost history's 51100 / 51200
 * rows: { id, name, cls, role, first, last, days, st, ot, hours, wages,
 * byDay: { d: hours }, lanes: { Mechanical, Electrical } (hours by where
 * they are coded) }. `cls` is the classification on their latest day.
 */
export const peopleOf = (jobCostDetail, laneOf = laneOfCode) => {
    const people = {};
    (Array.isArray(jobCostDetail) ? jobCostDetail : []).forEach((r) => {
        const ct = String(r?.Cost_Type_Code || '');
        if (ct !== '51100' && ct !== '51200') return;
        const id = empKey(r.Staff_ID);
        const d = ymd(r.Service_Date);
        if (!id || !d) return;
        const h = pNum(r.Total_Hours);
        const p = people[id] || (people[id] = { id, name: '', cls: '', clsDate: '', first: d, last: d, byDay: {}, st: 0, ot: 0, hours: 0, wages: 0, lanes: { Mechanical: 0, Electrical: 0 } });
        const name = String(r.Staff_First_Last_Name || '').trim();
        if (name && name !== 'Miscellaneous') p.name = name;
        const cls = String(r.Labor_Classification_Description || '').trim();
        if (cls && d >= p.clsDate) { p.cls = cls; p.clsDate = d; }
        p.wages += pNum(r.Job_Cost);
        if (!h) return;
        if (d < p.first) p.first = d;
        if (d > p.last) p.last = d;
        p.byDay[d] = (p.byDay[d] || 0) + h;
        if (ct === '51200') p.ot += h; else p.st += h;
        p.hours += h;
        const lane = laneOf(r.Phase_Code || r.Cost_Code);
        if (lane) p.lanes[lane] += h;
    });
    return Object.values(people).map((p) => ({ ...p, role: leadRoleOf(p.cls), days: Object.keys(p.byDay).length }));
};

/** Billable and cost on the Billing tab's priced lines, by employee number. */
export const billsByPerson = (txns) => {
    const out = {};
    (Array.isArray(txns) ? txns : []).forEach((t) => {
        const id = empKey(t && t.sid);
        if (!id || t.nb) return;
        const b = out[id] || (out[id] = { bill: 0, cost: 0 });
        b.bill += pNum(t.bill);
        b.cost += pNum(t.cost);
    });
    return out;
};

/**
 * A foreman's VoltaGrid Field reports (readVgFieldCrewDays days) rolled up
 * by employee number: { reports, people (average per report), hours, last }.
 */
export const crewsOf = (days) => {
    const out = {};
    (Array.isArray(days) ? days : []).forEach((d) => {
        const id = empKey(d && d.foremanEmp);
        if (!id) return;
        const c = out[id] || (out[id] = { reports: 0, peopleSum: 0, hours: 0, last: '' });
        const worked = new Set();
        let h = 0;
        (d.labor || []).forEach((l) => {
            const lh = pNum(l.st) + pNum(l.ot);
            if (lh > 0) { worked.add(empKey(l.emp) || l.name); h += lh; }
        });
        c.reports += 1;
        c.peopleSum += worked.size;
        c.hours += h;
        if (d.date > c.last) c.last = d.date;
    });
    Object.values(out).forEach((c) => { c.people = c.reports ? c.peopleSum / c.reports : 0; });
    return out;
};

/** How recently a foreman filed: 'week' (their last report is inside 7 days of today), 'fortnight' (inside 14), else null. */
export const reportRecency = (last, today) => {
    if (!last) return null;
    const age = moment(today, 'YYYY-MM-DD').diff(moment(last, 'YYYY-MM-DD'), 'days');
    if (age < 0) return 'week';
    return age < 7 ? 'week' : age < 14 ? 'fortnight' : null;
};

/** The lane a person's classification (JDE's, or the field roster's) names: "Electrical Foreman", "I&E Foreman", "Rig Superintendent"; null when it says nothing. */
export const laneWordOf = (cls, rosterCls) => {
    const s = `${cls || ''} | ${rosterCls || ''}`;
    if (ELECTRICAL_RE.test(s)) return 'Electrical';
    if (MECHANICAL_RE.test(s)) return 'Mechanical';
    return null;
};
/** The lane most of a person's hours are coded to; null when neither side has more. */
export const laneHoursOf = (p) => {
    const l = (p && p.lanes) || {};
    if ((l.Electrical || 0) > (l.Mechanical || 0)) return 'Electrical';
    if ((l.Mechanical || 0) > (l.Electrical || 0)) return 'Mechanical';
    return null;
};
/** The lane a person's own facts name: the PM's word, the classification's, then where the hours are coded; null when nothing says. */
export const laneSeedOf = (p, rosterCls, override) => (LANES.includes(override) ? override : laneWordOf(p.cls, rosterCls) || laneHoursOf(p));

/**
 * The leadership map. Each lead becomes a node { key, person, role, lane,
 * seed, under, parent, children, note }; a line runs from a person to the
 * one they are under (the roster's reportsTo, or the PM's `under`) when that
 * person is above them in rank — anything else is kept as a note. A chain's
 * lane is decided at its top: the PM's word, the top person's classification,
 * then the classifications under them (an electrical foreman says more about
 * a superintendent than the codes the superintendent's own hours sit on),
 * then the top person's hours, then the chain's hours, else Mechanical.
 *   leads     peopleOf() rows with a role (and whatever the caller added:
 *             onSite, jdeRole, added…)
 *   roster    readVgFieldCrewDays' people: [{ emp, name, cls, roles, reportsTo }]
 *   overrides { [id]: { lane?, under? } } — under '' means nobody
 * Returns { byId, lanes: [{ name, roots }] } where roots are the lane's
 * columned trees: a node at column 0 is a superintendent, 1 a general
 * foreman, 2 a foreman, and a step nobody fills is a { placeholder: true }
 * node holding the people below it.
 */
export const leadershipOf = (leads, roster = [], overrides = {}) => {
    const ros = {};
    (roster || []).forEach((r) => { const id = empKey(r && r.emp); if (id) ros[id] = r; });
    const byId = {};
    (leads || []).forEach((p) => {
        const r = ros[p.id];
        const ov = overrides[p.id] || {};
        const under = ov.under !== undefined && ov.under !== null ? empKey(ov.under) : empKey(r?.reportsTo);
        const role = roleWithRoster(p.role, r?.roles);
        const jdeRole = p.jdeRole === undefined ? leadRoleOf(p.cls) : p.jdeRole;
        byId[p.id] = {
            key: p.id, person: p, role, rosterRole: rosterRoleOf(r?.roles), lifted: role !== jdeRole && role === rosterRoleOf(r?.roles),
            set: LANES.includes(ov.lane) ? ov.lane : null, word: laneWordOf(p.cls, r?.cls), hoursLane: laneHoursOf(p),
            seed: laneSeedOf(p, r?.cls, ov.lane), laneSet: LANES.includes(ov.lane), underSet: ov.under !== undefined && ov.under !== null,
            under, lane: null, parent: null, children: [], note: '',
        };
    });
    Object.values(byId).forEach((n) => {
        if (!n.under) return;
        const up = byId[n.under];
        if (!up || up === n) { n.note = `Under ${ros[n.under]?.name || n.under} on the field roster, who is not on the map`; return; }
        if (rank(up.role) < rank(n.role)) { n.parent = up; up.children.push(n); return; }
        n.note = `Under ${up.person.name || up.key} on the field roster, a ${roleWord(up.role)} — not drawn above a ${roleWord(n.role)}`;
    });
    const descendants = (n) => n.children.flatMap((c) => [c, ...descendants(c)]);
    const roots = Object.values(byId).filter((n) => !n.parent);
    // What most of a chain says, weighed by hours so one busy electrical
    // foreman outweighs a part-timer; a head with no hours still counts.
    const most = (list, pick) => {
        const tally = { Mechanical: 0, Electrical: 0 };
        list.forEach((d) => { const l = pick(d); if (l) tally[l] += (d.person.hours || 0) + 1; });
        return tally.Electrical > tally.Mechanical ? 'Electrical' : tally.Mechanical > tally.Electrical ? 'Mechanical' : null;
    };
    roots.forEach((root) => {
        const below = descendants(root);
        const lane = root.set || root.word || most(below, (d) => d.set || d.word) || root.hoursLane || most(below, (d) => d.hoursLane) || 'Mechanical';
        root.lane = lane;
        below.forEach((d) => { d.lane = lane; });
    });
    const order = (a, b) => rank(a.role) - rank(b.role) || ((b.person.onSite ? 1 : 0) - (a.person.onSite ? 1 : 0)) || (b.person.hours || 0) - (a.person.hours || 0) || String(a.person.name).localeCompare(String(b.person.name));
    const columned = (list, col, keyBase) => {
        const here = list.filter((n) => rank(n.role) === col).sort(order).map((n) => ({ ...n, children: columned(n.children, col + 1, n.key) }));
        const deeper = list.filter((n) => rank(n.role) > col);
        if (deeper.length) {
            const key = `${keyBase}|none|${col}`;
            here.push({ key, placeholder: true, role: ROLES[col], lane: deeper[0].lane, children: columned(deeper, col + 1, key) });
        }
        return here;
    };
    const lanes = LANES.map((name) => ({ name, roots: columned(roots.filter((r) => r.lane === name), 0, name) }));
    return { byId, lanes };
};

/** The leads to draw: hidden people are left out unless the PM asks to see them. */
export const visibleLeads = (leads, showHidden = false) => (showHidden ? (leads || []) : (leads || []).filter((l) => !l.hidden));

/** The trees with off-site people cut away — unless someone under them is still on site, the PM added them by hand, or they are hidden and being shown. */
export const pruneOffSite = (list) => list
    .map((n) => ({ ...n, children: pruneOffSite(n.children) }))
    .filter((n) => n.children.length || (n.person && (n.person.onSite || n.person.added || n.person.hidden)));

const hoursIn = (byDay, start, end) => Object.keys(byDay).reduce((a, d) => a + (d >= start && d <= end ? byDay[d] : 0), 0);

const sessionEdits = {};
const editKey = (job, version, rowId) => `${job}|${version}|${rowId}`;

const ratingColors = (palette, rating) => {
    if (rating === 'Strong') return { color: '#166534', background: palette.greenLight };
    if (rating === 'On track') return { color: palette.blue700, background: palette.blue50 };
    if (rating === 'Needs attention') return { color: '#854d0e', background: palette.orangeLight };
    return { color: palette.muted, background: '#f1f5f9' };
};

const fmtD = (d) => (d ? moment(d, 'YYYY-MM-DD').format('M/D/YY') : '');
const fmtH = (h) => (h ? Number(h).toLocaleString(undefined, { maximumFractionDigits: 0 }) : '-');
const money = (n) => (n ? `$${fNum(n, 1)}` : '-');

const MAP_CSS = `
.vgl-map { --vgl-w: 200px; --vgl-gap: 28px; --vgl-line: var(--border-strong, #d4d4d4); overflow-x: auto; }
.vgl-head { display: flex; align-items: flex-end; padding: 0 0 6px 0; border-bottom: 1px solid var(--border, #e5e5e5); }
.vgl-lane-label { width: 108px; flex: none; padding-right: 12px; }
.vgl-col { width: var(--vgl-w); margin-right: var(--vgl-gap); flex: none; font-size: 11px; font-weight: 600; letter-spacing: .04em; text-transform: uppercase; color: var(--muted-foreground, #737373); }
.vgl-lane { display: flex; align-items: flex-start; padding: 12px 0; border-bottom: 1px solid var(--border, #e5e5e5); }
.vgl-lane:last-child { border-bottom: 0; }
.vgl-lane-name { font-size: 12px; font-weight: 600; letter-spacing: .04em; text-transform: uppercase; color: var(--foreground, #0a0a0a); padding-left: 10px; border-left: 3px solid var(--destructive, #d13138); line-height: 1.2; }
.vgl-lane-sub { font-size: 11px; color: var(--muted-foreground, #737373); margin-top: 4px; padding-left: 13px; line-height: 1.4; }
.vgl-roots { display: flex; flex-direction: column; min-width: 0; }
.vgl-root { padding: 4px 0; }
.vgl-node { display: flex; align-items: center; position: relative; }
.vgl-kids { display: flex; flex-direction: column; padding-left: var(--vgl-gap); position: relative; }
.vgl-kid { position: relative; padding: 4px 0; }
.vgl-kid::before { content: ''; position: absolute; left: calc(-1 * var(--vgl-gap) / 2); width: calc(var(--vgl-gap) / 2); top: 50%; border-top: 2px solid var(--vgl-line); }
.vgl-kid::after { content: ''; position: absolute; left: calc(-1 * var(--vgl-gap) / 2); top: 0; bottom: 0; border-left: 2px solid var(--vgl-line); }
.vgl-kid:first-child::after { top: 50%; }
.vgl-kid:last-child::after { bottom: 50%; }
.vgl-kid:only-child::after { display: none; }
.vgl-card { position: relative; box-sizing: border-box; width: var(--vgl-w); flex: none; text-align: left; font: inherit; font-size: 11px; line-height: 1.35; color: var(--foreground, #0a0a0a); background: var(--card, #fff); border: 1px solid var(--border, #e5e5e5); border-radius: var(--radius, 10px); box-shadow: var(--shadow-xs, 0 1px 2px 0 rgb(0 0 0 / .05)); padding: 6px 8px; cursor: pointer; transition: background-color .15s, border-color .15s, box-shadow .15s; }
.vgl-card:hover { border-color: var(--border-strong, #d4d4d4); box-shadow: var(--shadow-md, 0 4px 6px -1px rgb(0 0 0 / .08)); }
.vgl-card:focus-visible { outline: none; border-color: var(--ring, #1d518c); box-shadow: var(--focus-ring, 0 0 0 3px rgb(29 81 140 / .3)); }
.vgl-card.vgl-sel { background: var(--muted, #f5f5f5); border-color: var(--border-strong, #d4d4d4); box-shadow: inset 3px 0 0 var(--destructive, #d13138); }
.vgl-card.vgl-off { border-style: dashed; color: var(--text-secondary, #525252); }
.vgl-card.vgl-off .vgl-name { color: var(--text-secondary, #525252); }
.vgl-card.vgl-out::after { content: ''; position: absolute; left: 100%; top: 50%; width: calc(var(--vgl-gap) / 2); border-top: 2px solid var(--vgl-line); }
.vgl-ph { cursor: default; border-style: dashed; background: var(--subtle, #fafafa); box-shadow: none; color: var(--muted-foreground, #737373); font-style: italic; display: flex; align-items: center; min-height: 40px; }
.vgl-ph:hover { border-color: var(--border, #e5e5e5); box-shadow: none; }
.vgl-row1 { display: flex; align-items: center; gap: 6px; min-width: 0; }
.vgl-name { font-size: 12px; font-weight: 600; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; flex: 1 1 auto; min-width: 0; }
.vgl-badge { flex: none; display: inline-block; padding: 0 7px; border-radius: 9999px; font-size: 11px; font-weight: 500; line-height: 16px; white-space: nowrap; }
.vgl-cls { color: var(--muted-foreground, #737373); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.vgl-stats { color: var(--text-secondary, #525252); font-variant-numeric: tabular-nums; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; display: flex; align-items: center; gap: 5px; }
.vgl-dot { flex: none; width: 7px; height: 7px; border-radius: 50%; background: var(--border-strong, #d4d4d4); }
.vgl-dot.vgl-week { background: var(--success, #15803d); }
.vgl-dot.vgl-fortnight { background: var(--warning, #a16207); }
.vgl-empty { font-size: 11px; color: var(--muted-foreground, #737373); font-style: italic; padding: 8px 0; }
.vgl-detail { margin-top: 12px; border: 1px solid var(--border, #e5e5e5); border-radius: var(--radius-card, 14px); background: var(--subtle, #fafafa); padding: 12px 14px; }
.vgl-detail-head { display: flex; align-items: flex-start; justify-content: space-between; gap: 10px; flex-wrap: wrap; }
.vgl-facts { display: grid; grid-template-columns: repeat(auto-fill, minmax(118px, 1fr)); gap: 8px 14px; margin: 10px 0; }
.vgl-fact-l { font-size: 11px; color: var(--muted-foreground, #737373); }
.vgl-fact-v { font-size: 13px; font-weight: 600; font-variant-numeric: tabular-nums; color: var(--foreground, #0a0a0a); }
.vgl-fact-s { font-size: 11px; color: var(--text-secondary, #525252); }
.vgl-form { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
.vgl-form label { display: flex; align-items: center; gap: 5px; font-size: 11px; color: var(--muted-foreground, #737373); }
.vgl-legend { display: flex; align-items: center; gap: 12px; flex-wrap: wrap; font-size: 11px; color: var(--muted-foreground, #737373); margin-top: 8px; }
.vgl-legend span { display: inline-flex; align-items: center; gap: 5px; }
@media (prefers-reduced-motion: reduce) { .vgl-card { transition: none; } }
@media print { .vg-lead-review { page-break-inside: avoid !important; box-shadow: none !important; } .vgl-map { overflow: visible; } }
`;

function VoltaGridLeadership({ palette, jobUserData, selectedJob, selectedVersion, versionStart, billRev, locked, save }) {
    const [jobCostDetail] = useAtom(jobCostDetailAtom);
    // A job switch empties the cost history before the new job's read lands;
    // that is loading, not a job with nobody on it.
    const [loadingCost] = useAtom(loadingCostAtom);
    const costLoading = !Array.isArray(jobCostDetail) || (loadingCost && !jobCostDetail.length);
    const [crews, setCrews] = useState({ job: '', days: null, people: [], failed: false });
    const [showOff, setShowOff] = useState(false);
    const [showHidden, setShowHidden] = useState(false);
    const [selected, setSelected] = useState(null);
    const [reviews, setReviews] = useState({});
    const [note, setNote] = useState('');
    const [adding, setAdding] = useState(null); // { id, role } while the add form is open
    const [reload, setReload] = useState(0); // bumped by "retry" when the field reports did not load
    const today = moment().format('YYYY-MM-DD');

    const people = useMemo(() => peopleOf(jobCostDetail), [jobCostDetail]);
    const bills = useMemo(() => (billRev?.ready ? billsByPerson(billRev.txns) : null), [billRev]);

    // The labor week the page reports (the version's, or the newest JDE has
    // posted) and the TREND_WEEKS Monday–Sunday weeks ending with it.
    const weeks = useMemo(() => {
        const through = people.reduce((a, p) => (p.last > a ? p.last : a), '');
        const shown = laborWeeksFor(versionWeeks(versionStart), through);
        const last = shown.weeks[shown.weeks.length - 1];
        const mon = moment.utc(last.start, 'YYYY-MM-DD');
        const list = [];
        for (let i = TREND_WEEKS - 1; i >= 0; i -= 1) {
            const s = mon.clone().subtract(i * 7, 'days');
            list.push({ start: s.format('YYYY-MM-DD'), end: s.clone().add(6, 'days').format('YYYY-MM-DD') });
        }
        return { list, last, through };
    }, [people, versionStart]);

    // The foremen's field reports for the last REPORT_DAYS days — the
    // reports are live, where JDE's labor posts a week or more behind — and
    // the job's roster leads with who each is under.
    useEffect(() => {
        if (!selectedJob) return undefined;
        let gone = false;
        setCrews({ job: selectedJob, days: null, people: [], failed: false });
        setSelected(null);
        setAdding(null);
        const end = moment().format('YYYY-MM-DD');
        const start = moment().subtract(REPORT_DAYS - 1, 'days').format('YYYY-MM-DD');
        loadVgFieldCrewDays({ job: String(selectedJob), start, end })
            .then((res) => { if (!gone) setCrews({ job: selectedJob, days: Array.isArray(res?.days) ? res.days : [], people: Array.isArray(res?.people) ? res.people : [], failed: !res }); })
            .catch(() => { if (!gone) setCrews({ job: selectedJob, days: [], people: [], failed: true }); });
        return () => { gone = true; };
    }, [selectedJob, reload]);
    const crewByEmp = useMemo(() => crewsOf(crews.days), [crews.days]);
    const rosterByEmp = useMemo(() => {
        const out = {};
        (crews.people || []).forEach((r) => { const id = empKey(r && r.emp); if (id) out[id] = r; });
        return out;
    }, [crews.people]);

    // Saved reviews on the version, with this session's edits laid over.
    useEffect(() => {
        const out = {};
        (jobUserData || []).forEach((item) => {
            if (item.Forecast_Tab !== 'Overview' || String(item.Version_GUID) !== String(selectedVersion) || String(item.BU_ID) !== String(selectedJob)) return;
            if (item.Forecast_Field_Name !== 'VgLeadReview') return;
            out[empKey(item.Forecast_Row_ID)] = item.Forecast_Value || '';
        });
        const prefix = `${selectedJob}|${selectedVersion}|`;
        Object.keys(sessionEdits).forEach((k) => { if (k.startsWith(prefix)) out[k.slice(prefix.length)] = sessionEdits[k]; });
        const parsed = {};
        Object.entries(out).forEach(([id, v]) => { if (!v) return; try { parsed[id] = JSON.parse(v); } catch (e) { /* a cut row */ } });
        setReviews(parsed);
    }, [jobUserData, selectedJob, selectedVersion]);
    const persist = (id, rec) => {
        // `under: ''` (nobody) is a real choice, kept; everything else empty drops out.
        const clean = rec ? Object.fromEntries(Object.entries(rec).filter(([k, v]) => v !== undefined && v !== null && (v !== '' || k === 'under'))) : null;
        const empty = !clean || !Object.keys(clean).length;
        const value = empty ? '' : JSON.stringify(clean);
        sessionEdits[editKey(selectedJob, selectedVersion, id)] = value;
        setReviews((p) => { const n = { ...p }; if (empty) delete n[id]; else n[id] = clean; return n; });
        save('VgLeadReview', value, id);
    };
    const update = (id, patch) => persist(id, { ...(reviews[id] || {}), ...patch });

    // The leads: JDE's superintendents, general foremen and foremen, plus
    // anyone the PM added (a role saved on the review), who may be on the
    // job's labor under another classification or only on the field roster.
    const leads = useMemo(() => {
        const byId = {};
        people.forEach((p) => { byId[p.id] = p; });
        const ids = new Set(people.filter((p) => p.role).map((p) => p.id));
        Object.entries(reviews).forEach(([id, r]) => { if (ROLES.includes(r?.role)) ids.add(id); });
        return [...ids].map((id) => {
            const r = reviews[id] || {};
            const p = byId[id] || { id, name: rosterByEmp[id]?.name || id, cls: rosterByEmp[id]?.cls || '', role: null, first: '', last: '', byDay: {}, st: 0, ot: 0, hours: 0, wages: 0, days: 0, lanes: { Mechanical: 0, Electrical: 0 } };
            const onSite = hoursIn(p.byDay, weeks.last.start, weeks.last.end) > 0;
            const b = bills ? bills[p.id] : null;
            const role = ROLES.includes(r.role) ? r.role : p.role;
            return {
                ...p, onSite, role: roleWithRoster(role, rosterByEmp[p.id]?.roles), jdeRole: p.role, added: ROLES.includes(r.role), hidden: !!r.hidden, onLabor: !!byId[id],
                trend: weeks.list.map((w) => hoursIn(p.byDay, w.start, w.end)),
                otPct: p.hours ? p.ot / p.hours : 0,
                billsAt: b && b.cost > 0 ? b.bill / b.cost : null,
                crew: crewByEmp[p.id] || null,
            };
        });
    }, [people, reviews, weeks, bills, crewByEmp, rosterByEmp]);

    // The map: lanes of chains, from the roster's lines and the PM's.
    // Hidden people are not on it (so nobody is drawn under them) unless
    // "Show N hidden" is on.
    const drawn = useMemo(() => visibleLeads(leads, showHidden), [leads, showHidden]);
    const map = useMemo(() => leadershipOf(drawn, crews.people, reviews), [drawn, crews.people, reviews]);
    const laneCounts = useMemo(() => {
        const out = {};
        LANES.forEach((name) => { out[name] = { Superintendent: 0, 'General Foreman': 0, Foreman: 0 }; });
        Object.values(map.byId).forEach((n) => { if ((n.person.onSite || n.person.added) && !n.person.hidden && out[n.lane]) out[n.lane][n.role] += 1; });
        return out;
    }, [map]);
    const offCount = leads.filter((l) => !l.onSite && !l.added && !l.hidden).length;
    const hiddenCount = leads.filter((l) => l.hidden).length;
    const selectedLead = selected ? leads.find((l) => l.id === selected) : null;
    const selectedNode = selected ? map.byId[selected] : null;
    // The note is typed locally and saved on blur or Enter; a new selection loads its own.
    useEffect(() => { setNote(selected ? (reviews[selected]?.note || '') : ''); }, [selected]);

    // People who could be added: everyone on the job's labor JDE does not
    // call a lead, and the roster's people not on the labor.
    const candidates = useMemo(() => {
        const onMap = new Set(leads.map((l) => l.id));
        const list = people.filter((p) => !onMap.has(p.id) && p.name).map((p) => ({ id: p.id, name: p.name, cls: p.cls, where: '' }));
        const seen = new Set(list.map((c) => c.id));
        (crews.people || []).forEach((r) => { const id = empKey(r.emp); if (id && !onMap.has(id) && !seen.has(id)) list.push({ id, name: r.name || id, cls: r.cls || '', where: 'field roster' }); });
        return list.sort((a, b) => a.name.localeCompare(b.name));
    }, [people, leads, crews.people]);

    const input = { boxSizing: 'border-box', height: 28, padding: '2px 6px', fontSize: 12, fontFamily: 'inherit', border: `1px solid ${palette.border}`, borderRadius: 6, outline: 'none', background: '#fff', color: palette.text };
    const btn = (primary) => ({ height: 28, padding: '0 10px', fontSize: 11, cursor: 'pointer', border: primary ? 'none' : `1px solid ${palette.border}`, borderRadius: 6, fontWeight: 600, background: primary ? palette.blue600 : 'transparent', color: primary ? '#fff' : palette.text });
    const weekLabel = `WE ${fmtD(weeks.last.end)}`;
    const linkBtn = { font: 'inherit', fontSize: 11, fontWeight: 600, color: palette.blue700, background: 'transparent', border: 0, padding: 0, cursor: 'pointer' };

    // --- the map ---------------------------------------------------------
    const card = (node) => {
        const p = node.person;
        const rec = reviews[p.id];
        const sel = selected === p.id;
        const stats = p.onLabor ? [`${fmtH(p.hours)} h`, p.hours ? `${(p.otPct * 100).toFixed(0)}% OT` : null, bills && p.billsAt != null ? `${p.billsAt.toFixed(2)}×` : null].filter(Boolean).join(' · ') : 'No JDE labor on the job';
        const recency = p.crew ? reportRecency(p.crew.last, today) : null;
        const crew = crews.days == null ? '…' : p.crew ? `${p.crew.reports} report${p.crew.reports === 1 ? '' : 's'} · ${p.crew.people.toFixed(1)} crew · ${moment(p.crew.last, 'YYYY-MM-DD').format('M/D')}` : (p.role === 'Foreman' ? 'No field reports' : null);
        const hint = [p.cls, node.lifted ? `${node.role} on the field roster` : null, p.added ? 'Added to the map here' : null, p.hidden ? 'Hidden from the map' : null, node.note || null, rec?.note || null].filter(Boolean).join('\n');
        return (
            <button type="button" className={`vgl-card${sel ? ' vgl-sel' : ''}${(p.onSite || p.added) && !p.hidden ? '' : ' vgl-off'}${node.children.length ? ' vgl-out' : ''}`}
                onClick={() => { setSelected(sel ? null : p.id); setAdding(null); }} title={hint} aria-pressed={sel}>
                <div className="vgl-row1">
                    <span className="vgl-name">{p.name || p.id}</span>
                    {rec?.rating && <span className="vgl-badge" style={ratingColors(palette, rec.rating)}>{rec.rating}</span>}
                </div>
                <div className="vgl-cls">{p.cls || (p.added ? 'Added here' : '')}{p.hidden ? ' · hidden' : p.onSite || p.added ? '' : ' · off site'}</div>
                <div className="vgl-stats">{stats}</div>
                {crew && (
                    <div className="vgl-stats" title={p.crew ? `Last VoltaGrid Field report ${fmtD(p.crew.last)}` : undefined}>
                        {p.crew && <span className={`vgl-dot${recency ? ` vgl-${recency}` : ''}`} />}
                        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{crew}</span>
                    </div>
                )}
            </button>
        );
    };
    const nodeView = (node) => (
        <div className="vgl-node" key={node.key}>
            {node.placeholder
                ? <div className={`vgl-card vgl-ph${node.children.length ? ' vgl-out' : ''}`}>No {roleWord(node.role)} assigned</div>
                : card(node)}
            {node.children.length > 0 && (
                <div className="vgl-kids">
                    {node.children.map((c) => <div className="vgl-kid" key={c.key}>{nodeView(c)}</div>)}
                </div>
            )}
        </div>
    );
    const laneView = (lane) => {
        const roots = showOff ? lane.roots : pruneOffSite(lane.roots);
        const c = laneCounts[lane.name];
        return (
            <div className="vgl-lane" key={lane.name}>
                <div className="vgl-lane-label">
                    <div className="vgl-lane-name">{lane.name}</div>
                    <div className="vgl-lane-sub">{c.Superintendent} supt · {c['General Foreman']} GF · {c.Foreman} foremen<br />on site {weekLabel}</div>
                </div>
                <div className="vgl-roots">
                    {roots.length ? roots.map((r) => <div className="vgl-root" key={r.key}>{nodeView(r)}</div>) : <div className="vgl-empty">No one on site {weekLabel}.</div>}
                </div>
            </div>
        );
    };
    const fact = (label, value, sub) => (
        <div key={label}><div className="vgl-fact-l">{label}</div><div className="vgl-fact-v">{value}</div>{sub && <div className="vgl-fact-s">{sub}</div>}</div>
    );

    // The detail panel: the person's numbers, and the PM's choices, each
    // saved as it is changed.
    const detail = selectedLead && selectedNode && (() => {
        const l = selectedLead; const n = selectedNode; const rec = reviews[l.id] || {};
        const autoUnder = rosterByEmp[l.id]?.reportsTo ? (map.byId[empKey(rosterByEmp[l.id].reportsTo)]?.person.name || rosterByEmp[l.id].reportsTo) : 'nobody';
        const above = Object.values(map.byId).filter((x) => rank(x.role) < rank(n.role)).sort((a, b) => rank(a.role) - rank(b.role) || String(a.person.name).localeCompare(String(b.person.name)));
        const saveNote = () => { if ((rec.note || '') !== note) update(l.id, { note }); };
        return (
            <div className="vgl-detail">
                <div className="vgl-detail-head">
                    <div>
                        <div style={{ fontSize: 14, fontWeight: 600 }}>{l.name || l.id}{(l.hidden || (!l.onSite && !l.added)) && <span style={{ marginLeft: 6, fontSize: 11, fontWeight: 500, color: palette.muted }}>{l.hidden ? 'hidden' : 'off site'}</span>}</div>
                        <div style={{ fontSize: 12, color: palette.muted }}>
                            {l.cls || 'No JDE classification'}{n.lifted ? ` · ${n.role} on the field roster` : ''}{l.added ? ` · added here as ${roleWord(n.role)}` : ''} · {n.lane} lane{n.laneSet ? ' (set here)' : ''}
                            {n.parent ? ` · under ${n.parent.person.name}${n.underSet ? ' (set here)' : ''}` : n.underSet ? ' · under nobody (set here)' : ' · nobody above on the roster'}
                            {n.note ? ` · ${n.note}` : ''}
                        </div>
                    </div>
                    <button className="no-print" onClick={() => setSelected(null)} style={btn(false)}>Close</button>
                </div>
                <div className="vgl-facts">
                    {fact('On site', l.first ? `${moment(l.first, 'YYYY-MM-DD').format('M/D')} – ${moment(l.last, 'YYYY-MM-DD').format('M/D')}` : '-', `${l.days} days`)}
                    {fact('Hours', fmtH(l.hours), `ST ${fmtH(l.st)} · OT ${fmtH(l.ot)}`)}
                    {fact('OT %', l.hours ? `${(l.otPct * 100).toFixed(0)}%` : '-')}
                    {weeks.list.map((w, i) => fact(`WE ${moment(w.end, 'YYYY-MM-DD').format('M/D')}`, fmtH(l.trend[i]), 'hours'))}
                    {fact('Wages', money(l.wages))}
                    {fact('Bills at', bills ? (l.billsAt != null ? `${l.billsAt.toFixed(2)}×` : '-') : '…', 'billable ÷ cost')}
                    {fact(`Reports, ${REPORT_DAYS} days`, crews.days == null ? '…' : l.crew ? l.crew.reports : '-', l.crew ? `last ${fmtD(l.crew.last)}` : null)}
                    {fact('Avg crew', l.crew ? l.crew.people.toFixed(1) : '-', l.crew ? `${fmtH(l.crew.hours)} crew hours` : null)}
                    {fact('Hours by lane', `${fmtH(l.lanes.Mechanical)} / ${fmtH(l.lanes.Electrical)}`, 'mechanical / electrical')}
                </div>
                {locked ? (
                    <div style={{ fontSize: 12, color: palette.text }}>
                        {rec.rating && <span className="vgl-badge" style={{ ...ratingColors(palette, rec.rating), marginRight: 6 }}>{rec.rating}</span>}
                        <span style={{ color: rec.note ? palette.text : palette.muted, fontStyle: rec.note ? 'normal' : 'italic' }}>{rec.note || 'No review'}</span>
                    </div>
                ) : (
                    <div className="vgl-form no-print">
                        <label>Under
                            <select value={rec.under === undefined ? AUTO : rec.under} onChange={(e) => update(l.id, { under: e.target.value === AUTO ? undefined : e.target.value })} style={{ ...input, width: 180 }} title="Who this person is drawn under">
                                <option value={AUTO}>Roster ({autoUnder})</option>
                                <option value="">Nobody</option>
                                {above.map((x) => <option key={x.key} value={x.key}>{x.person.name || x.key} ({x.role === 'General Foreman' ? 'GF' : 'Supt'})</option>)}
                            </select>
                        </label>
                        <label>Lane
                            <select value={LANES.includes(rec.lane) ? rec.lane : AUTO} onChange={(e) => update(l.id, { lane: e.target.value === AUTO ? undefined : e.target.value })} style={{ ...input, width: 150 }} title="The lane this person's chain sits on; under someone else, their chain's lane applies">
                                <option value={AUTO}>Auto ({n.lane})</option>
                                {LANES.map((x) => <option key={x} value={x}>{x}</option>)}
                            </select>
                        </label>
                        {l.added && (
                            <label>Role
                                <select value={n.role} onChange={(e) => update(l.id, { role: e.target.value })} style={{ ...input, width: 150 }}>
                                    {ROLES.map((x) => <option key={x} value={x}>{x}</option>)}
                                </select>
                            </label>
                        )}
                        <label>Rating
                            <select value={rec.rating || ''} onChange={(e) => update(l.id, { rating: e.target.value || undefined })} style={{ ...input, width: 140 }}>
                                <option value="">None</option>
                                {RATINGS.map((r) => <option key={r} value={r}>{r}</option>)}
                            </select>
                        </label>
                        <input value={note} maxLength={NOTE_MAX} placeholder="Note" onChange={(e) => setNote(e.target.value)} onBlur={saveNote}
                            onKeyDown={(e) => { if (e.key === 'Enter') saveNote(); }} style={{ ...input, flex: '1 1 220px' }} />
                        {l.added
                            ? <button onClick={() => { persist(l.id, { ...rec, role: undefined, under: undefined, lane: undefined, hidden: undefined }); setSelected(null); }} style={btn(false)} title="Take this person off the map">Remove</button>
                            : l.hidden
                                ? <button onClick={() => { update(l.id, { hidden: undefined }); setSelected(null); }} style={btn(false)} title="Draw this person on the map again">Show on map</button>
                                : <button onClick={() => { update(l.id, { hidden: true }); setSelected(null); }} style={btn(false)} title="Take this person off the map; 'Show hidden' under the map brings them back">Hide</button>}
                    </div>
                )}
            </div>
        );
    })();

    const addForm = adding && (
        <div className="vgl-detail no-print" style={{ margin: '0 0 12px' }}>
            <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 8 }}>Add a person to the map</div>
            <div className="vgl-form">
                <select value={adding.id} onChange={(e) => setAdding((a) => ({ ...a, id: e.target.value }))} style={{ ...input, width: 300 }}>
                    <option value="">Who…</option>
                    {candidates.map((c) => <option key={c.id} value={c.id}>{c.name}{c.cls ? ` — ${c.cls}` : ''}{c.where ? ` (${c.where})` : ''}</option>)}
                </select>
                <select value={adding.role} onChange={(e) => setAdding((a) => ({ ...a, role: e.target.value }))} style={{ ...input, width: 160 }}>
                    {ROLES.map((x) => <option key={x} value={x}>{x}</option>)}
                </select>
                <button disabled={!adding.id} onClick={() => { update(adding.id, { role: adding.role }); setSelected(adding.id); setAdding(null); }} style={{ ...btn(true), opacity: adding.id ? 1 : 0.5 }}>Add</button>
                <button onClick={() => setAdding(null)} style={btn(false)}>Cancel</button>
            </div>
        </div>
    );

    return (
        <div className="vg-lead-review" style={{ background: palette.card, borderRadius: 10, border: `1px solid ${palette.border}`, boxShadow: '0 4px 16px rgba(15,23,42,.06)', padding: 16, marginBottom: 14 }}>
            <style>{MAP_CSS}</style>
            <h3 style={{ margin: '0 0 10px', fontSize: 11, fontWeight: 700, color: palette.text, letterSpacing: '-0.01em', textTransform: 'uppercase' }}>Field Leadership</h3>

            {costLoading ? (
                <div style={{ fontSize: 11, color: palette.muted, padding: '6px 2px' }}>Loading the cost history…</div>
            ) : (
                <>
                    {leads.length === 0 && <div style={{ fontSize: 11, color: palette.muted, fontStyle: 'italic', padding: '2px 0 8px' }}>No superintendent, general foreman or foreman on the job's labor yet.</div>}
                    {addForm}
                    <div className="vgl-map">
                        <div className="vgl-head">
                            <div className="vgl-lane-label" />
                            {ROLES.map((r) => <div className="vgl-col" key={r}>{rolePlural(r)}</div>)}
                        </div>
                        {map.lanes.map(laneView)}
                    </div>
                    <div className="vgl-legend">
                        <span><span className="vgl-dot vgl-week" /> report in the last 7 days</span>
                        <span><span className="vgl-dot vgl-fortnight" /> in the last 14</span>
                        <span><span className="vgl-dot" /> older</span>
                        <span>· on site = hours in {weekLabel}</span>
                        {crews.failed && <span>· field reports unavailable <button className="no-print" onClick={() => setReload((n) => n + 1)} style={linkBtn}>retry</button></span>}
                        <span className="no-print" style={{ marginLeft: 'auto', gap: 12 }}>
                            {offCount > 0 && <button onClick={() => setShowOff((v) => !v)} style={linkBtn}>{showOff ? 'Hide' : 'Show'} {offCount} off site</button>}
                            {hiddenCount > 0 && <button onClick={() => { setShowHidden((v) => !v); setSelected(null); }} style={linkBtn}>{showHidden ? 'Hide' : 'Show'} {hiddenCount} hidden</button>}
                            {!locked && <button onClick={() => { setAdding(adding ? null : { id: '', role: 'Foreman' }); setSelected(null); }} style={linkBtn}>{adding ? 'Cancel adding' : 'Add person'}</button>}
                        </span>
                    </div>
                    {detail}
                </>
            )}
        </div>
    );
}

export default VoltaGridLeadership;
